import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, EmptyState, QueryStateBoundary } from "@/components/ui";
import { useCampaigns, useCampaignStats } from "@/api/hooks/useCampaigns";
import {
  CAMPAIGN_STATUS_BADGE_VARIANT,
  CAMPAIGN_STATUS_LABELS,
  type Campaign,
} from "@/api/types/campaign";
import { CampaignBuilderDialog } from "./CampaignBuilderDialog";

export function CampaignsPage() {
  const navigate = useNavigate();
  const [builderOpen, setBuilderOpen] = useState(false);

  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCampaigns();

  const campaigns = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  function handleSaved(campaign: Campaign) {
    setBuilderOpen(false);
    navigate(`/campaigns/${campaign.id}`);
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-text-primary">Campaigns</h1>
          <p className="mt-1 text-sm text-text-muted">
            Email campaigns to a segment of your contacts — recipients are always limited to
            contacts who opted in to email.
          </p>
        </div>
        <Button onClick={() => setBuilderOpen(true)}>New campaign</Button>
      </div>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {campaigns.length === 0 ? (
          <EmptyState
            icon="📧"
            title="No campaigns yet"
            description="Build a segment, write your email, and send — Signature Pour handles the rest."
            action={{ label: "New campaign", onClick: () => setBuilderOpen(true) }}
          />
        ) : (
          <>
            <ul className="divide-y divide-border rounded-lg border border-border bg-bg-card">
              {campaigns.map((campaign) => (
                <CampaignListRow
                  key={campaign.id}
                  campaign={campaign}
                  onOpen={() => navigate(`/campaigns/${campaign.id}`)}
                />
              ))}
            </ul>
            {hasNextPage && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="secondary"
                  onClick={() => void fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? "Loading..." : "Load more"}
                </Button>
              </div>
            )}
          </>
        )}
      </QueryStateBoundary>

      <CampaignBuilderDialog
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
}

interface CampaignListRowProps {
  campaign: Campaign;
  onOpen: () => void;
}

function CampaignListRow({ campaign, onOpen }: CampaignListRowProps) {
  // Sent/opened/clicked only exist once a campaign has actually gone out —
  // fetching stats for a draft would just 404 or return zeros for nothing.
  const showStats = campaign.status === "sent" || campaign.status === "sending";
  const { data: stats } = useCampaignStats(showStats ? campaign.id : undefined);

  return (
    <li>
      <button
        type="button"
        data-campaign-id={campaign.id}
        onClick={onOpen}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-bg-hover"
      >
        <div>
          <p className="font-medium text-text-primary">{campaign.name}</p>
          <p className="text-xs text-text-muted">{campaign.subject}</p>
        </div>
        <div className="flex items-center gap-3">
          {stats && (
            <span className="text-xs text-text-muted">
              {stats.sent} sent · {stats.opened} opened · {stats.clicked} clicked
            </span>
          )}
          <Badge variant={CAMPAIGN_STATUS_BADGE_VARIANT[campaign.status]}>
            {CAMPAIGN_STATUS_LABELS[campaign.status]}
          </Badge>
        </div>
      </button>
    </li>
  );
}
